import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import {
  ActivatedRoute,
  Data,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import { WINDOW } from '@personal-area/common';
import { EventType, GA4Event, GTMService } from '@personal-area/global/gtm';
import { filter } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PageSpeedService {
  private _router = inject(Router);
  private _activatedRoute = inject(ActivatedRoute);
  private _gtmService = inject(GTMService);
  private _platformId = inject(PLATFORM_ID);
  private _window = inject(WINDOW);

  private _navigationStart$ = this._router.events
    .pipe(filter(evt => evt instanceof NavigationStart));

  private _navigationEnd$ = this._router.events
    .pipe(filter(evt =>
      evt instanceof NavigationEnd ||
      evt instanceof NavigationCancel ||
      evt instanceof NavigationError,
    ));

  private _enabled = isPlatformBrowser(this._platformId) && Boolean(this._window.performance);
  private _start = 0;
  private _duration = 0;
  private _manualStart = false;

  constructor() {
    if (!this._enabled) {
      return;
    }

    this._navigationStart$
      .subscribe(() => {
        if (this._manualStart) {
          this._manualStart = false;
          return;
        }

        this._startMeasurement();
      });

    this._navigationEnd$
      .subscribe((event) => {
        const data = this._getRouteData();

        if (data?.['pageSpeedAutoComplete'] !== false) {
          this.complete(event.url);
        }
      });
  }

  public start(): void {
    if (!this._enabled) {
      return;
    }

    this._manualStart = true;
    this._startMeasurement();
  }

  public complete(url?: string): void {
    if (!this._enabled) {
      return;
    }

    if (this._start === 0) {
      return;
    }

    this._duration = performance.now() - this._start;

    this._gtmService.event(EventType.PageSpeed, <GA4Event>{
      context: 'performance',
      object: url ?? this._window.location.pathname.replace(/^\/cabinet/, ''),
      action: 'completed',
      duration: this._duration,
    });

    this._reset();
  }

  private _startMeasurement(): void {
    if (this._duration !== 0) {
      this._reset();
    }

    this._start = performance.now();
  }

  private _reset() {
    this._start = 0;
    this._duration = 0;
  }

  private _getRouteData(): Data | undefined {
    let route = this._activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }

    return route.routeConfig?.data;
  }
}
